<template>
  <div>
    <DescriptionCard />
    <LineScoreMultipleChart
      v-if="parvandehItems.length > 0"
      :items="parvandehItems"
    />
    <ParvandehCard :showBodyFn="handleShowBodyFn">
      <template slot="center-col" slot-scope="{ item }">
        <div
          v-if="item.jobPosition || item.jobLevel || item.jobCategory"
          class="font-size-12"
        >
          <div class="font-weight-500 color-complementary-200">
            {{ $lang.job }}
          </div>
          <div v-if="item.jobPosition" class="color-complementary-300">
            {{ `${$lang.title}: ${item.jobPosition}` }}
          </div>
          <div v-if="item.jobLevel" class="color-complementary-300">
            {{ `${$lang.level}: ${item.jobLevel}` }}
          </div>
          <div v-if="item.jobCategory" class="color-complementary-300">
            {{ `${$lang.jobCategory}: ${item.jobCategory}` }}
          </div>
        </div>
      </template>
      <template slot="left-col" slot-scope="{ item }">
        <div
          v-if="item.educationField || item.educationGrade"
          class="font-size-12"
        >
          <div class="font-weight-500 color-complementary-200">
            {{ $lang.final_report_education }}
          </div>
          <div v-if="item.educationField" class="color-complementary-300">
            {{ `${$lang.grade}: ${item.educationField}` }}
          </div>
          <div v-if="item.educationGrade" class="color-complementary-300">
            {{ `${$lang.level}: ${item.educationGrade}` }}
          </div>
        </div>
      </template>
      <template slot="body" slot-scope="{ item, bodyData }">
        <b-row class="margin-0">
          <b-col
            cols="12"
            class="font-size-14 color-complementary-400 margin-bottom-16"
          >
            {{ $lang.reports }}
          </b-col>
          <b-col
            v-for="(report, index) in generateReports(bodyData)"
            :key="`a-${index}`"
            cols="12"
            sm="4"
            md="4"
            class="padding-4"
          >
            <div
              v-if="report.requestType === 'service'"
              @click="viewReport(report)"
              class="padding-16 background-surface border-radius-8 border--solid border--complementary-50 cursor--pointer"
            >
              <b-img-lazy
                class="width-percent-100 height-115 object-fit--contain border--solid border--complementary-50 border-radius-8 background-light-gray margin-bottom-8"
                :src="report.previewImage || ''"
                :onerror="`this.src='${defaultImage}'`"
              />
              <div class="flex-center color-complementary-300">
                <span
                  class="font-size-14 margin-left-8 text-ellipsis"
                  v-b-tooltip.hover
                  :title="report.caption"
                >
                  {{ report.caption }}
                </span>
                <span>
                  <mdicon name="eye" size="24" />
                </span>
              </div>
            </div>
            <a
              v-else
              :href="report.downloadLink"
              target="_black"
              class="text-decoration-none"
            >
              <div
                class="padding-16 background-surface border-radius-8 border--solid border--complementary-50 cursor--pointer"
              >
                <b-img-lazy
                  class="width-percent-100 height-115 object-fit--contain border--solid border--complementary-50 border-radius-8 background-light-gray margin-bottom-8"
                  :src="report.previewImage || ''"
                  :onerror="`this.src='${defaultImage}'`"
                />
                <div class="flex-center color-complementary-300">
                  <span
                    class="font-size-14 margin-left-8 text-ellipsis"
                    v-b-tooltip.hover
                    :title="report.caption"
                  >
                    {{ report.caption }}
                  </span>
                  <span>
                    <mdicon name="tray-arrow-down" size="24" />
                  </span>
                </div>
              </div>
            </a>
          </b-col>
          <b-col
            cols="12"
            v-if="!generateReports(bodyData).length"
            class="font-size-14 color-complementary-300 text-center"
          >
            {{ $lang.reportListEmptyText }}
          </b-col>
        </b-row>
      </template>
    </ParvandehCard>
  </div>
</template>

<script>
import { generateConfig, goToRoute } from "@/shared/utils";
import DescriptionCard from "../components/DescriptionCard.vue";
import ParvandehCard from "../components/parvandeh-card/ParvandehCard.vue";
import LineScoreMultipleChart from "../components/LineScoreMultipleChart.vue";
import { pathOr } from "ramda";
import { getParvandehReportList } from "@/services";
import { inject, onMounted } from "vue";

export default {
  components: {
    DescriptionCard,
    ParvandehCard,
    LineScoreMultipleChart,
  },
  setup() {
    const defaultImage = generateConfig(["images", "shared", "noImage"]);

    const generateArrowSeriesData = (item) => {
      let performance = pathOr(8, ["performance"], item);
      performance = performance >= 8 ? performance : 8;
      let score = (performance * 11) / 71;
      score = Math.floor(score);
      const data = new Array(score - 1).fill(null);
      data.push(6);
      return data;
    };

    const arrowDownImage = generateConfig([
      "images",
      "shared",
      "redesign",
      "arrowDown",
    ]);

    const handleShowBodyFn = async (item) => {
      const { code } = item;
      // eslint-disable-next-line no-useless-catch
      try {
        const response = await getParvandehReportList(code);
        const { items } = response;
        return items;
      } catch (error) {
        throw error;
      }
    };

    const generateReports = (items) => {
      const reports = items || [];
      return reports.map((report) => {
        return {
          id: pathOr(null, ["report_detail_id"], report),
          requestType: pathOr(null, ["request_type"], report),
          downloadLink: pathOr(null, ["download_link"], report),
          caption: pathOr("", ["report_caption"], report),
          pageName: pathOr(null, ["front_page_name"], report),
          parvandehCode: pathOr(null, ["parvandeh", "code"], report),
          resourceName: pathOr(null, ["resource", "name"], report),
          resourceCode: pathOr(null, ["resource", "code"], report),
          examPackId: pathOr(null, ["exam_pack", "id"], report),
          previewImage: pathOr("", ["preview_image"], report),
          azmoonExecutePersonCode: pathOr(
            null,
            ["azmoon_execute_person_code"],
            report
          ),
        };
      });
    };

    const viewReport = (item) => {
      const isCenterExecute = item.resourceName === "center_execute";
      const resourceCode = isCenterExecute
        ? item.resourceCode // use in kanoon module
        : item.azmoonExecutePersonCode; // use in azmoon module

      const name = `${item.pageName}-report`;
      const params = {
        parvandehCode: item.parvandehCode,
        examPackId: item.examPackId, // use in azmoon module
        resourceCode,
      };
      goToRoute(name, params);
    };

    const parvandehItems = inject("PARVANDEH_ITEMS");
    const getParvandehData = inject("GET_PARVANDEH_DATA");
    onMounted(() => getParvandehData());

    // return data to be used in template
    return {
      defaultImage,
      generateArrowSeriesData,
      arrowDownImage,
      handleShowBodyFn,
      generateReports,
      pathOr,
      viewReport,
      parvandehItems,
    };
  },
};
</script>
